import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Collapse, Row, Col, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons'; // Importing Pencil icon
import APICom from '../../utils/helpers/APICom'; // Assuming APICom is your custom API helper

const GeneralSettingsComponent = ({ data }) => {
  const [open, setOpen] = useState(true);
  const [apiData, setApiData] = useState(null); // State to store the API response
  const [loading, setLoading] = useState(false); // Loading state
  const [isEditing, setIsEditing] = useState(false); // Track if the table is in editing mode
  const [editedData, setEditedData] = useState(null); // Store modified data separately
  const xCancelSourceForGrid = useRef(null);

  // API call based on the data prop
  const fetchApiData = async () => {
    if (data) {
      setLoading(true); // Start loading
      try {
        const response = await APICom.fetchData(
          'GenericManifestDotNetCoreURL',
          "api/Settings/GetGeneralMVRouteSettings?iSettingID=" + data,
          'get',
          {}
        );

        if (response?.data?.Table) {
          const tableData = response.data.Table;
          setApiData(tableData); // Store the Table data in the state
          setEditedData(JSON.parse(JSON.stringify(tableData))); // Make a deep copy of the table data
          setIsEditing(false);
        } else {
          console.log('No data received from the API.');
        }
      } catch (error) {
        console.error('Error fetching API data:', error);
      } finally {
        setLoading(false); // End loading
      }
    }
  };

  useEffect(() => {
    fetchApiData(); // Call the API when data prop changes or component mounts
  }, [data]); // Run effect when data prop changes

  // Handle cell value changes
  const handleCellChange = (index, field, value) => {
    const updatedData = [...editedData];
    updatedData[index][field] = value;
    setEditedData(updatedData); // Update the editedData state
  };

  // Reset editedData to the original data (cancel the edits)
  const handleCancel = () => {
    setIsEditing(false); // Exit editing mode
    setEditedData(JSON.parse(JSON.stringify(apiData))); // Reset to a deep copy of the original apiData
  };

  // Save the changes (make API call or update the server)
  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        iSettingID: data,
        dRevenuePerDRM: editedData[0].dRevenuePerDRM || 0,
        dOptimalThreshold: editedData[0].dOptimalThreshold || 0,
        dMinRevDRMPerTrip: editedData[0].dMinRevDRMPerTrip || 0,
      };

      xCancelSourceForGrid.current = APICom.GenerateSource();

      const response = await APICom.fetchData(
        'GenericManifestDotNetCoreURL',
        "api/Settings/UpdateMVRouteGeneralSettings",
        'post',
        {},
        payload,
        xCancelSourceForGrid.current
      );

      if (response?.data) {
        console.log('Data updated successfully');
        setIsEditing(false); // Turn off editing mode
        setApiData([...editedData]); // Update apiData with the changes
      } else {
        console.log('Failed to update data');
      }
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mb-4">
      <Card.Header>
        <Card.Title className="text-center mb-4">Cost-Revenue Settings</Card.Title>

        <Button onClick={() => setOpen(!open)} aria-controls="example-collapse-text" aria-expanded={open}>
          {open ? '-' : '+'}
        </Button>
      </Card.Header>
      <Collapse in={open}>
        <Card.Body>
          {/* Loading state */}
          <div>
            {loading && <p>Loading data...</p>}
            {!loading && !apiData && <p>Select an Algorithm setting.</p>}
          </div>

          {/* Grid Layout */}
          <Row className="mb-2">
            <Col className="fs-6"><strong>Action</strong></Col>
            <Col className="fs-6"><strong>Acceptable Cost Rev Threshold</strong></Col>
            <Col className="fs-6"><strong>Rev Per Mile of DRM</strong></Col>
            <Col className="fs-6"><strong>Min Rev DRM</strong></Col>
          </Row>

          {apiData && apiData.length > 0 ? (
            editedData.map((item, index) => (
              <Row key={index} className="mb-2">
                {/* Pencil Icon in place of # */}
                <Col>
                  {!isEditing && !loading && (
                    <Button variant="link" onClick={() => setIsEditing(true)}>
                      <FontAwesomeIcon icon={faPencilAlt} size="lg" color="black" />
                    </Button>
                  )}
                </Col>

                {/* Editable cell for dOptimalThreshold */}
                <Col>
                  {isEditing ? (
                    <Form.Control
                      type="number"
                      value={item.dOptimalThreshold || 0}
                      onChange={(e) => handleCellChange(index, 'dOptimalThreshold', e.target.value)}
                    />
                  ) : (
                    item.dOptimalThreshold || 0
                  )}
                </Col>

                {/* Editable cell for dRevenuePerDRM */}
                <Col>
                  {isEditing ? (
                    <Form.Control
                      type="number"
                      value={item.dRevenuePerDRM || 0}
                      onChange={(e) => handleCellChange(index, 'dRevenuePerDRM', e.target.value)}
                    />
                  ) : (
                    item.dRevenuePerDRM || 0
                  )}
                </Col>

                {/* Editable cell for dMinRevDRMPerTrip */}
                <Col>
                  {isEditing ? (
                    <Form.Control
                      type="number"
                      value={item.dMinRevDRMPerTrip || 0}
                      onChange={(e) => handleCellChange(index, 'dMinRevDRMPerTrip', e.target.value)}
                    />
                  ) : (
                    item.dMinRevDRMPerTrip || 0
                  )}
                </Col>
              </Row>
            ))
          ) : (
            <Row>
              <Col colSpan={4}>Select an Algorithm setting.</Col>
            </Row>
          )}
<div className="d-flex justify-content-center gap-3">
          {/* Save button */}
          {isEditing && !loading && (
            <div className="text-center">
              <Button variant="success" onClick={handleSave}>Save Changes</Button>
            </div>
          )}

          {/* Cancel button */}
          {isEditing && !loading && (
            <div className="text-center">
              <Button variant="danger" onClick={handleCancel}>Cancel</Button>
            </div>
          )}
          </div>
        </Card.Body>
      </Collapse>
    </Card>
  );
};

export default GeneralSettingsComponent;
